// Content for the local SEO landing pages (/fresh-vegetables-karachi and
// friends), rendered by src/pages/LocalLanding.jsx and pre-rendered into
// real HTML by scripts/prerender.js.
//
// WHY THESE PAGES EXIST
// ---------------------
// The shop and product pages compete on product names ("Brinjal"), not on
// how people actually search ("fresh vegetables for sale in Karachi").
// These pages target that commercial-intent local phrasing directly.
//
// THE RULE FOR ADDING MORE: every page here must earn its place with
// information a customer genuinely wants - what's in season, what the
// delivery charge to their area actually is, how to judge freshness. Six
// near-identical pages with the suburb name swapped is a doorway-page
// pattern, which Google treats as spam and which would put the whole domain
// at risk. If a new page can't say anything the others don't, don't add it.
//
// Everything below must stay factually true to how the shop actually
// operates - delivery zones and charges here mirror worker/src/distance.js
// and worker/src/pricing.js (10km / 30km / 50km at Rs 150 / 300 / 500, with
// zone 2 and 3 requiring a Rs 5,000+ order).

export const DELIVERY_SUMMARY = {
  zone1: { maxKm: 10, charge: 150 },
  zone2: { maxKm: 30, charge: 300 },
  zone3: { maxKm: 50, charge: 500 },
  bulkThreshold: 5000
}

const deliverySection = {
  heading: 'Delivery areas and charges',
  paragraphs: [
    'Delivery is priced on the real road distance between our shop on Khayaban-e-Shujaat, Shah Rasool Colony and your address - not a flat city-wide rate and not straight-line distance, which badly underestimates real travel in Karachi where creeks and the coastline force long detours.',
    'Within 10 km the charge is Rs. 150 and there is no minimum order. Between 10 and 30 km it is Rs. 300, and between 30 and 50 km it is Rs. 500 - those two bands need an order of Rs. 5,000 or more, which is why they mostly suit bulk and family orders. You drop a pin at checkout and see your exact distance, zone and charge before you pay.'
  ]
}

export const LOCAL_LANDING_PAGES = [
  {
    slug: 'fresh-vegetables-karachi',
    category: 'vegetables',
    eyebrow: 'Vegetables',
    h1: 'Fresh Vegetables for Sale in Karachi',
    metaTitle: 'Fresh Vegetables for Sale in Karachi - Same & Next Day Delivery',
    metaDescription:
      'Buy fresh vegetables in Karachi, picked and packed only after you order. Live per-kg prices, delivery priced on real road distance, same-day and next-day slots.',
    intro: [
      'We sell fresh vegetables in Karachi the way a good sabzi shop always has - sorted the morning they arrive, weighed in front of you, and packed only once someone has actually ordered them. Nothing is pre-bagged the night before to sit under lights.',
      'Because we are a wholesale vegetable centre first, the range follows the mandi rather than a fixed catalogue. What you see listed is what genuinely came in.'
    ],
    sections: [
      {
        heading: 'How to tell vegetables are actually fresh',
        paragraphs: [
          'Weight is the honest test. A fresh gourd, brinjal or cucumber feels heavy for its size because it still holds water; the same vegetable three days later feels light and slightly hollow. Leafy greens should snap rather than bend - if palak or methi drapes limply over your hand, it has been cut for a while.',
          'For tomatoes and capsicum, look at the stem end rather than the skin. Skin can be polished; a dried, shrunken stem cannot be faked. Karachi heat works fast, which is why we cut delivery time down rather than relying on cold storage to hide age.'
        ]
      },
      {
        heading: 'Storing vegetables in Karachi heat',
        paragraphs: [
          'Most of the year the mistake is refrigerating everything. Tomatoes, onions, potatoes, garlic and ginger all keep better out of the fridge in a dark, ventilated spot - refrigeration turns tomatoes mealy and makes potatoes sweet and grainy.',
          'Leafy greens are the opposite: wash, dry properly, wrap in a dry cloth and refrigerate. Wet leaves rot within a day here. Keep onions and potatoes apart - onions release moisture and gas that sprouts potatoes early.'
        ]
      },
      deliverySection
    ],
    faqs: [
      {
        question: 'Do you deliver fresh vegetables across Karachi?',
        answer:
          'Yes. We deliver up to 50 km by road from our shop in Shah Rasool Colony, which covers most of Karachi. Orders within 10 km have no minimum; beyond that a Rs. 5,000 minimum applies.'
      },
      {
        question: 'Are the vegetables washed before delivery?',
        answer:
          'We sort and clean off field soil but do not wet-wash produce before packing. Washing before storage traps moisture and accelerates rotting in Karachi humidity - wash just before cooking instead.'
      },
      {
        question: 'Can I order vegetables in wholesale quantity?',
        answer:
          'Yes. We are a wholesale vegetable centre, so bulk quantities for households, hostels, restaurants and events are normal for us. Larger orders also unlock the longer-distance delivery zones.'
      }
    ]
  },
  {
    slug: 'fresh-fruits-karachi',
    category: 'fruits',
    eyebrow: 'Fruits',
    h1: 'Fresh Fruits for Sale in Karachi',
    metaTitle: 'Fresh Fruits for Sale in Karachi - Seasonal Fruit Delivery',
    metaDescription:
      'Buy fresh seasonal fruit in Karachi with home delivery. Mangoes, bananas, apples, papaya and more, packed after you order and priced per kg.',
    intro: [
      'Fruit is the category where freshness is most obvious and most often faked. We buy to the season rather than stocking the same list year-round, so what is listed reflects what is genuinely good right now.',
      'Everything is packed after your order is placed, which matters more for fruit than anything else we sell - ripe fruit handled repeatedly bruises, and bruised fruit spoils from the inside.'
    ],
    sections: [
      {
        heading: 'What is in season in Karachi',
        paragraphs: [
          'Mango season runs roughly May through August and is the one time of year worth buying heavily. Sindhri arrives first and is best eaten slightly soft; Chaunsa follows and holds sweetness longer. Outside those months, imported mango is expensive and rarely worth it.',
          'Bananas, papaya and apples are dependable all year. Citrus - kinnow, malta, lemons - is at its best in the cooler months from roughly November to February, when it is both cheaper and noticeably juicier.'
        ]
      },
      {
        heading: 'Ripening and storing fruit here',
        paragraphs: [
          'Karachi warmth ripens fruit faster than most people expect. Buy mangoes and bananas slightly under-ripe and let them finish at home in a paper bag, out of direct sun. Refrigerating an unripe mango stops it ripening properly and leaves it starchy.',
          'Keep bananas away from everything else. They release ethylene, which will ripen - and then over-ripen - whatever is sitting next to them within a day or two.'
        ]
      },
      deliverySection
    ],
    faqs: [
      {
        question: 'Is your fruit ripe on delivery or does it need a day?',
        answer:
          'It depends on the fruit. Bananas and mangoes are usually sent slightly firm so they finish ripening at home without bruising in transit. Papaya, apples and citrus are sent ready to eat.'
      },
      {
        question: 'Do you sell mangoes by the crate in Karachi?',
        answer:
          'Yes, during mango season we sell by crate as well as by kilo. Crate quantities also help meet the Rs. 5,000 minimum for the longer delivery zones.'
      },
      {
        question: 'Can I choose how ripe my fruit is?',
        answer:
          'Add a note with your order. Because we pack after the order comes in rather than pre-bagging, we can usually pick riper or firmer fruit to suit when you plan to eat it.'
      }
    ]
  },
  {
    slug: 'fresh-chicken-karachi',
    category: 'chicken',
    eyebrow: 'Chicken',
    h1: 'Fresh Chicken for Sale in Karachi',
    metaTitle: 'Fresh Chicken for Sale in Karachi - Cut to Order, Delivered',
    metaDescription:
      'Fresh chicken in Karachi, cut only after you order - karahi cut, boneless, mince, drumsticks and more. No frozen stock. Same-day and next-day delivery.',
    intro: [
      'Chicken is where "fresh" is claimed most and meant least. Ours is cut after your order is placed, in the cut you choose, rather than portioned in advance and held in a chiller waiting for a buyer.',
      'That single difference is what you taste. Chicken that has sat pre-cut loses moisture from every cut surface, which is why it tightens and dries in the pan no matter how carefully you cook it.'
    ],
    sections: [
      {
        heading: 'Choosing the right cut',
        paragraphs: [
          'Karahi cut is bone-in and medium-sized, which is what you want for karahi and most curries - the bone carries flavour into the masala and stops the meat drying out over high heat. Biryani cut is slightly larger so the pieces survive layering and steaming without shredding.',
          'Boneless breast is the leanest and the least forgiving; take it off the heat at 74°C and let it rest. Boneless leg cubes stay far juicier and are the better choice for grilling, skewers and anything cooked hot and fast. Mince should be used within a day or two - it has the most cut surface, so it ages fastest.'
        ]
      },
      {
        heading: 'How to judge fresh chicken',
        paragraphs: [
          'Fresh chicken is faintly moist but not slippery, and it should smell of almost nothing. Any sourness means it has been sitting, regardless of colour. A slight pink tinge near the bone is normal and is not a freshness problem.',
          'Be sceptical of chicken that looks unusually plump and glossy in trays - that is often water retention from chilling rather than quality. The most reliable question is not how it looks but when it was cut.'
        ]
      },
      deliverySection
    ],
    faqs: [
      {
        question: 'Is your chicken frozen?',
        answer:
          'No. We do not keep frozen or pre-cut chicken stock. Birds are prepared after your order is placed and delivered the same day or on your chosen next-day slot.'
      },
      {
        question: 'Can I choose the cut?',
        answer:
          'Yes - karahi cut, biryani cut, qorma cut, whole, boneless breast, boneless leg cubes, drumsticks, wings, mince and more. Because cutting happens after ordering, the cut is your choice rather than whatever was portioned earlier.'
      },
      {
        question: 'How long does fresh chicken keep at home?',
        answer:
          'Use it within one to two days refrigerated. If you need longer, freeze it the day it arrives rather than after a couple of days in the fridge - freezing already-aged chicken locks in the decline.'
      }
    ]
  },
  {
    slug: 'fresh-beef-karachi',
    category: 'beef',
    eyebrow: 'Beef',
    h1: 'Fresh Beef for Sale in Karachi',
    metaTitle: 'Fresh Beef for Sale in Karachi - Boneless, Qeema, Boti Delivered',
    metaDescription:
      'Fresh beef in Karachi cut to order - boneless, boti, qeema and more. Prepared after you order, never pre-packed. Home delivery across Karachi.',
    intro: [
      'Beef rewards proper handling more than any other meat we sell. Ours is trimmed and cut to order - boneless, boti or qeema - rather than sitting portioned in a display chiller.',
      'Qeema in particular should never be bought pre-ground and stored. Mincing multiplies the surface area enormously, and pre-ground meat that has been sitting is the single most common cause of an off smell when the packet is opened.'
    ],
    sections: [
      {
        heading: 'Which cut for which dish',
        paragraphs: [
          'Boti - bite-sized cubes - is what you want for skewers, tikka and anything grilled. Marinate for at least two to four hours; overnight is better, and papaya or yoghurt in the marinade genuinely helps tenderise.',
          'Boneless is the flexible everyday choice for curries, bhuna and stews, and it slices cleanly for stir-fries. Qeema suits kheema curry, kebabs, samosas and stuffed parathas, and it cooks fast enough for a weeknight.'
        ]
      },
      {
        heading: 'How to tell beef is fresh',
        paragraphs: [
          'Fresh beef is deep red on the outside and can look darker inside where air has not reached - that darker colour is normal, not spoilage. What should worry you is a grey-brown cast across the whole surface together with a sour smell.',
          'The fat tells you a lot. It should be creamy white and firm. Yellowing, greasy fat that smears when pressed means the meat has been sitting, whatever the lean parts look like.'
        ]
      },
      deliverySection
    ],
    faqs: [
      {
        question: 'Is the beef halal?',
        answer:
          'Yes. All meat we sell is halal, sourced and prepared according to standard halal practice.'
      },
      {
        question: 'Is the qeema ground fresh?',
        answer:
          'Yes - qeema is minced after your order is placed, not scooped from a pre-ground batch. That is the main reason it does not develop the sour smell people associate with packaged mince.'
      },
      {
        question: 'Can I order beef in bulk for an event?',
        answer:
          'Yes. Bulk orders for events, aqiqah and family gatherings are routine, and larger orders also meet the Rs. 5,000 minimum required for the 10-50 km delivery zones.'
      }
    ]
  },
  {
    slug: 'fresh-meat-karachi',
    category: null,
    categories: ['chicken', 'beef'],
    eyebrow: 'Meat',
    h1: 'Fresh Meat for Sale in Karachi',
    metaTitle: 'Fresh Meat for Sale in Karachi - Chicken & Beef Cut to Order',
    metaDescription:
      'Fresh halal meat in Karachi - chicken and beef cut only after you order. No frozen or pre-cut stock. Delivery across Karachi with same-day slots.',
    intro: [
      'We sell fresh chicken and beef in Karachi, both cut to order. If you are comparing meat shops online, the question worth asking any of them is simple: is the meat cut before or after the order arrives?',
      'Almost every shop calls itself fresh. Very few will tell you when the meat was actually cut, because for most the honest answer is "some time before you asked".'
    ],
    sections: [
      {
        heading: 'Why cut-to-order matters more here',
        paragraphs: [
          'Every cut surface is where meat loses moisture and where bacteria start. Meat portioned in advance has been losing both for hours or days before it reaches you, which is why it tightens in the pan and why the packet sometimes smells sour on opening.',
          'Karachi weather compresses all of this. The gap between preparation and cooking matters far more here than in a cooler climate, which is why we shorten that gap rather than relying on cold storage to disguise it.'
        ]
      },
      {
        heading: 'Cold storage is not the same as fresh',
        paragraphs: [
          'Frozen and chilled meat is not unsafe - it is simply not fresh in any meaningful sense. It is meat that was prepared at some earlier point and held. For slow-cooked dishes the difference is small. For karahi, tikka, grills and anything where texture carries the dish, it is obvious.',
          'If a shop cannot tell you when the meat was cut, that in itself is the answer.'
        ]
      },
      deliverySection
    ],
    faqs: [
      {
        question: 'What meat do you sell?',
        answer:
          'Fresh chicken and fresh beef, in a wide range of cuts. Both are halal and both are prepared after your order is placed rather than kept as pre-cut stock.'
      },
      {
        question: 'Do you sell frozen meat?',
        answer:
          'No. We keep no frozen or pre-cut meat stock. Everything is prepared once your order comes in.'
      },
      {
        question: 'How fast can I get meat delivered in Karachi?',
        answer:
          'Same-day delivery is available in hourly slots from 9 AM to 9 PM, and you can also book a next-day slot. A fast-delivery option places you in the next available slot.'
      }
    ]
  },
  {
    slug: 'grocery-delivery-karachi',
    category: null,
    categories: ['vegetables', 'fruits', 'chicken', 'beef'],
    eyebrow: 'Delivery',
    h1: 'Grocery Delivery in Karachi',
    metaTitle: 'Grocery Delivery in Karachi - Fresh Meat, Fruit & Vegetables',
    metaDescription:
      'Fresh grocery delivery across Karachi. Meat, chicken, fruit and vegetables prepared after you order. Delivery charges based on real road distance, not flat rates.',
    intro: [
      'We deliver fresh meat, chicken, fruit and vegetables across Karachi from our shop on Khayaban-e-Shujaat in Shah Rasool Colony. Orders are prepared after they come in, so delivery timing and freshness are the same problem rather than two separate ones.',
      'Delivery is charged on real road distance rather than a flat rate, which means nearby customers are not subsidising cross-city runs.'
    ],
    sections: [
      deliverySection,
      {
        heading: 'Delivery slots and timing',
        paragraphs: [
          'Same-day and next-day delivery are both available, in hourly slots from 9 AM to 9 PM. You choose the slot at checkout. A fast-delivery option puts your order into the next available slot for an additional Rs. 100.',
          'Because everything is cut, weighed and packed after your order arrives, choosing a slot closer to when you actually plan to cook gives you the freshest result.'
        ]
      },
      {
        heading: 'How ordering works',
        paragraphs: [
          'Add what you need, then drop a pin on the map at checkout. The pin is what sets your delivery charge - we look up the genuine road distance from the shop, show you the distance, zone and fee, and only then take the order.',
          'Payment is cash on delivery. Every order also comes with a four-digit verification code shown on your confirmation page; the rider confirms it on arrival so you know the order is yours.'
        ]
      }
    ],
    faqs: [
      {
        question: 'Which areas of Karachi do you deliver to?',
        answer:
          'Anywhere within 50 km by road of Shah Rasool Colony, which covers most of Karachi. Within 10 km there is no minimum order; between 10 and 50 km a Rs. 5,000 minimum applies.'
      },
      {
        question: 'How much is delivery in Karachi?',
        answer:
          'Rs. 150 within 10 km, Rs. 300 from 10 to 30 km, and Rs. 500 from 30 to 50 km. The exact charge is calculated from real road distance and shown before you confirm the order.'
      },
      {
        question: 'What payment methods do you accept?',
        answer:
          'Cash on delivery. Online payment is being added and will appear at checkout once it is live.'
      },
      {
        question: 'Can I collect from the shop instead?',
        answer:
          'Yes. You are welcome to buy directly at the shop on Khayaban-e-Shujaat, Shah Rasool Colony, Karachi.'
      }
    ]
  }
]

export const findLandingPage = (slug) => LOCAL_LANDING_PAGES.find((p) => p.slug === slug)
